import { motion, useReducedMotion } from 'framer-motion';
import { animationVariants, defaultViewport } from '../constants/animations';

export function Reveal({
  children,
  variant = 'slideUp',
  className = '',
  delay = 0,
  index = 0,
  as = 'div',
  viewport,
  animateOnMount = false,
  ...props
}) {
  const reduceMotion = useReducedMotion();
  const Component = motion[as] || motion.div;

  // animateOnMount: animate in once when the element mounts, independent of
  // scroll position — used for section titles so they can never re-hide
  // after a viewport-intersection edge case during fast/inertial scrolling.
  const scrollTriggerProps = animateOnMount
    ? { animate: 'visible' }
    : {
        whileInView: reduceMotion ? undefined : 'visible',
        viewport: viewport || defaultViewport,
      };

  return (
    <Component
      className={className}
      initial={reduceMotion ? false : 'hidden'}
      variants={animationVariants[variant]}
      transition={{ delay: delay || index * 0.04 }}
      {...scrollTriggerProps}
      {...props}
    >
      {children}
    </Component>
  );
}

export function StaggerGroup({
  children,
  className = '',
  slow = false,
  as = 'div',
  viewport,
  ...props
}) {
  const reduceMotion = useReducedMotion();
  const Component = motion[as] || motion.div;
  const containerVariant = slow
    ? animationVariants.staggerContainerSlow
    : animationVariants.staggerContainer;

  return (
    <Component
      className={className}
      initial={reduceMotion ? false : 'hidden'}
      whileInView={reduceMotion ? undefined : 'visible'}
      viewport={viewport || defaultViewport}
      variants={containerVariant}
      {...props}
    >
      {children}
    </Component>
  );
}

export function StaggerItem({
  children,
  variant = 'slideUp',
  className = '',
  delay = 0,
  index = 0,
  as = 'div',
  ...props
}) {
  const reduceMotion = useReducedMotion();
  const Component = motion[as] || motion.div;

  return (
    <Component
      className={className}
      variants={reduceMotion ? undefined : animationVariants[variant]}
      transition={{ delay: delay || index * 0.04 }}
      {...props}
    >
      {children}
    </Component>
  );
}

export function ScrollUnderline({ className = '', animateOnMount = false }) {
  const reduceMotion = useReducedMotion();
  const scrollTriggerProps = animateOnMount
    ? { animate: { scaleX: 1 } }
    : { whileInView: reduceMotion ? undefined : { scaleX: 1 }, viewport: defaultViewport };

  return (
    <motion.div
      className={className}
      aria-hidden="true"
      initial={reduceMotion ? false : { scaleX: 0 }}
      style={{ transformOrigin: 'left center' }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      {...scrollTriggerProps}
    />
  );
}
